import { 
  collection, 
  getDocs, 
  doc, 
  getDoc, 
  query, 
  where, 
  orderBy,
  limit,
  Timestamp,
  addDoc,
  updateDoc,
  deleteDoc,
  increment
} from 'firebase/firestore';
import { db } from '../firebase';

const BLOG_POSTS_COLLECTION = 'blogPosts';

/**
 * Convert Firestore timestamp to JavaScript Date
 */
const convertTimestamp = (timestamp) => {
  if (timestamp?.toDate) {
    return timestamp.toDate();
  }
  return timestamp;
};

/**
 * Convert Firestore document to blog post object
 */
const convertDocToPost = (doc) => {
  const data = doc.data();
  return {
    id: doc.id,
    ...data,
    publishedAt: convertTimestamp(data.publishedAt),
  };
};

/**
 * Fetch all blog posts, ordered by published date (newest first)
 */
export const getAllBlogPosts = async () => {
  try {
    if (!db) {
      console.error('Firestore database is not initialized. Please check your Firebase configuration.');
      return [];
    }
    
    const postsRef = collection(db, BLOG_POSTS_COLLECTION);
    
    // Try to get posts with ordering first
    try {
      const q = query(postsRef, orderBy('publishedAt', 'desc'));
      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map(doc => convertDocToPost(doc));
    } catch (queryError) {
      // If orderBy fails (no index or no documents), try without orderBy
      if (queryError.code === 'failed-precondition' || queryError.code === 9) {
        console.warn('OrderBy query requires an index. Fetching without order. Error:', queryError.message);
        try {
          const querySnapshot = await getDocs(postsRef);
          const posts = querySnapshot.docs.map(doc => convertDocToPost(doc));
          // Sort manually if we got posts
          return posts.sort((a, b) => {
            const dateA = a.publishedAt instanceof Date ? a.publishedAt : new Date(a.publishedAt);
            const dateB = b.publishedAt instanceof Date ? b.publishedAt : new Date(b.publishedAt);
            return dateB - dateA;
          });
        } catch (fetchError) {
          console.error('Error fetching posts without orderBy:', fetchError);
          return [];
        }
      }
      
      // Handle permission denied
      if (queryError.code === 'permission-denied' || queryError.code === 7) {
        console.error('Permission denied. Please check your Firestore security rules to allow reads.');
        return [];
      }
      
      // Handle other errors
      console.error('Error fetching blog posts:', queryError);
      return [];
    }
  } catch (error) {
    console.error('Unexpected error fetching blog posts:', error);
    return [];
  }
};

/**
 * Fetch a single blog post by slug
 */
export const getBlogPostBySlug = async (slug) => {
  try {
    const postsRef = collection(db, BLOG_POSTS_COLLECTION);
    const q = query(postsRef, where('slug', '==', slug), limit(1));
    const querySnapshot = await getDocs(q);
    
    if (querySnapshot.empty) {
      return null;
    }
    
    return convertDocToPost(querySnapshot.docs[0]);
  } catch (error) {
    console.error('Error fetching blog post by slug:', error);
    throw error;
  }
};

/**
 * Fetch a single blog post by ID
 */
export const getBlogPostById = async (id) => {
  try {
    const postRef = doc(db, BLOG_POSTS_COLLECTION, id);
    const postSnap = await getDoc(postRef);
    
    if (!postSnap.exists()) {
      return null;
    }
    
    return convertDocToPost(postSnap);
  } catch (error) {
    console.error('Error fetching blog post by ID:', error);
    throw error;
  }
};

/**
 * Search blog posts by title and content
 */
export const searchBlogPosts = async (searchTerm) => {
  try {
    const allPosts = await getAllBlogPosts();
    const term = searchTerm.toLowerCase();
    
    return allPosts.filter(post => 
      post.title.toLowerCase().includes(term) ||
      post.content.toLowerCase().includes(term) ||
      post.excerpt?.toLowerCase().includes(term)
    );
  } catch (error) {
    console.error('Error searching blog posts:', error);
    throw error;
  }
};

/**
 * Filter blog posts by category
 */
export const getBlogPostsByCategory = async (category) => {
  try {
    const postsRef = collection(db, BLOG_POSTS_COLLECTION);
    const q = query(
      postsRef, 
      where('category', '==', category),
      orderBy('publishedAt', 'desc')
    );
    const querySnapshot = await getDocs(q);
    
    return querySnapshot.docs.map(doc => convertDocToPost(doc));
  } catch (error) {
    console.error('Error fetching posts by category:', error);
    throw error;
  }
};

/**
 * Filter blog posts by tag
 */
export const getBlogPostsByTag = async (tag) => {
  try {
    const postsRef = collection(db, BLOG_POSTS_COLLECTION);
    const q = query(
      postsRef, 
      where('tags', 'array-contains', tag),
      orderBy('publishedAt', 'desc')
    );
    const querySnapshot = await getDocs(q);
    
    return querySnapshot.docs.map(doc => convertDocToPost(doc));
  } catch (error) {
    console.error('Error fetching posts by tag:', error);
    throw error;
  }
};

/**
 * Get all unique categories from blog posts
 */
export const getAllCategories = async () => {
  try {
    const posts = await getAllBlogPosts();
    const categories = [...new Set(posts.map(post => post.category).filter(Boolean))];
    return categories.sort();
  } catch (error) {
    console.error('Error fetching categories:', error);
    // Return empty array instead of throwing
    return [];
  }
};

/**
 * Get all unique tags from blog posts
 */
export const getAllTags = async () => {
  try {
    const posts = await getAllBlogPosts();
    const allTags = posts.flatMap(post => post.tags || []);
    const uniqueTags = [...new Set(allTags)];
    return uniqueTags.sort();
  } catch (error) {
    console.error('Error fetching tags:', error);
    // Return empty array instead of throwing
    return [];
  }
};


/**
 * Create a slug from a title
 */
export const createSlug = (title) => {
  return title
    .toLowerCase()
    .trim()
    .replace(/[^\w\s-]/g, '')
    .replace(/[\s_-]+/g, '-')
    .replace(/^-+|-+$/g, '');
};

/**
 * Check if a slug already exists
 */
export const slugExists = async (slug, excludeId = null) => {
  try {
    const existingPost = await getBlogPostBySlug(slug);
    if (!existingPost) return false;
    if (excludeId && existingPost.id === excludeId) return false;
    return true;
  } catch (error) {
    console.error('Error checking slug:', error);
    return false;
  }
};

/**
 * Create a new blog post
 */
export const createBlogPost = async (postData) => {
  try {
    if (!db) {
      throw new Error('Firestore database is not initialized');
    }

    let slug = postData.slug || createSlug(postData.title);
    
    let counter = 1;
    const originalSlug = slug;
    while (await slugExists(slug)) {
      slug = `${originalSlug}-${counter}`;
      counter++;
    }

    const postRef = collection(db, BLOG_POSTS_COLLECTION);
    const newPost = {
      title: postData.title,
      slug: slug,
      excerpt: postData.excerpt || '',
      content: postData.content || '',
      author: postData.author || 'Dr. Muyabins',
      publishedAt: postData.publishedAt || Timestamp.now(),
      category: postData.category || 'General',
      tags: postData.tags || [],
      featured: postData.featured || false,
      imageUrl: postData.imageUrl || ''
    };

    const docRef = await addDoc(postRef, newPost);
    return { id: docRef.id, ...newPost };
  } catch (error) {
    console.error('Error creating blog post:', error);
    throw error;
  }
};

/**
 * Update an existing blog post
 */
export const updateBlogPost = async (postId, postData) => {
  try {
    if (!db) {
      throw new Error('Firestore database is not initialized');
    }

    const postRef = doc(db, BLOG_POSTS_COLLECTION, postId);
    
    let slug = postData.slug;
    if (postData.title && !slug) {
      slug = createSlug(postData.title);
      if (await slugExists(slug, postId)) {
        let counter = 1;
        const originalSlug = slug;
        while (await slugExists(slug, postId)) {
          slug = `${originalSlug}-${counter}`;
          counter++;
        }
      }
    }

    const updateData = {};
    if (postData.title !== undefined) updateData.title = postData.title;
    if (slug !== undefined) updateData.slug = slug;
    if (postData.excerpt !== undefined) updateData.excerpt = postData.excerpt;
    if (postData.content !== undefined) updateData.content = postData.content;
    if (postData.author !== undefined) updateData.author = postData.author;
    if (postData.publishedAt !== undefined) {
      updateData.publishedAt = postData.publishedAt instanceof Date 
        ? Timestamp.fromDate(postData.publishedAt)
        : postData.publishedAt;
    }
    if (postData.category !== undefined) updateData.category = postData.category;
    if (postData.tags !== undefined) updateData.tags = postData.tags;
    if (postData.featured !== undefined) updateData.featured = postData.featured;
    if (postData.imageUrl !== undefined) updateData.imageUrl = postData.imageUrl;

    await updateDoc(postRef, updateData);
    return await getBlogPostById(postId);
  } catch (error) {
    console.error('Error updating blog post:', error);
    throw error;
  }
};

/**
 * Delete a blog post
 */
export const deleteBlogPost = async (postId) => {
  try {
    if (!db) {
      throw new Error('Firestore database is not initialized');
    }

    const postRef = doc(db, BLOG_POSTS_COLLECTION, postId);
    await deleteDoc(postRef);
    return true;
  } catch (error) {
    console.error('Error deleting blog post:', error);
    throw error;
  }
};

/**
 * Increment view count for a blog post
 * Uses Firestore increment for atomic updates
 */
export const incrementViewCount = async (postId) => {
  try {
    if (!db) {
      console.error('Firestore database is not initialized');
      return;
    }

    const postRef = doc(db, BLOG_POSTS_COLLECTION, postId);
    
    // Use increment to atomically update viewCount
    // If viewCount doesn't exist, it will be created with value 1
    await updateDoc(postRef, {
      viewCount: increment(1)
    });
  } catch (error) {
    // If document doesn't exist or viewCount field doesn't exist, handle gracefully
    if (error.code === 'not-found' || error.message?.includes('No document')) {
      try {
        // Try to create the viewCount field if document exists
        const postRef = doc(db, BLOG_POSTS_COLLECTION, postId);
        await updateDoc(postRef, {
          viewCount: 1
        });
      } catch (createError) {
        console.error('Error creating viewCount field:', createError);
      }
    } else {
      console.error('Error incrementing view count:', error);
    }
  }
};
